/** Sidebar navigation, ported from mockups/assets/app.js `NAV` (+ permission + agentScoped). */
import type { IconName } from "./icons";
import type { PermissionKey } from "./permissions";
import { adminRoutes, consoleRoutes } from "./routes";

/** Runtime counters that may render as a nav badge; values come from the API. */
export type NavBadgeSource = "inbox.open" | "gaps.open" | "support.open";
export type NavBadgeTone = "brand" | "accent";

export interface NavRouteContext {
  readonly ws: string;
  readonly agentId: string;
}

export interface NavSection {
  readonly kind: "section";
  readonly id: string;
  readonly label: string;
  /** i18n catalog key (packages/i18n `nav.*`). */
  readonly i18nKey: string;
}

export interface NavLink<TId extends string = string> {
  readonly kind: "link";
  readonly id: TId;
  readonly label: string;
  /** i18n catalog key (packages/i18n `nav.*`). */
  readonly i18nKey: string;
  /** The workspace's own word for this entry (its vocabulary), shown instead of the translated label when set. */
  readonly displayLabel?: string;
  readonly icon: IconName;
  readonly to: (ctx: NavRouteContext) => string;
  readonly badgeSource?: NavBadgeSource;
  readonly badgeTone?: NavBadgeTone;
}

export type TenantNavId =
  | "dashboard"
  | "inbox"
  | "analytics"
  | "playground"
  | "knowledge"
  | "knowledge-graph"
  | "agent-settings"
  | "actions"
  | "evaluations"
  | "widget-studio"
  | "webapp-studio"
  | "channels"
  | "gaps"
  | "leads"
  | "workflows"
  | "announcements"
  | "wallboard"
  | "tickets"
  | "organisations"
  | "contacts"
  | "assets"
  | "installations"
  | "visits"
  | "my-day"
  | "orders"
  | "catalogue"
  | "service-reports"
  | "service-settings"
  | "audiences"
  | "team"
  | "billing"
  | "settings"
  | "guide";

export type AdminNavId =
  | "dashboard"
  | "tenants"
  | "usage"
  | "plans"
  | "sales-requests"
  | "ai-models"
  | "system"
  | "support"
  | "audit"
  | "staff"
  | "settings";

export interface TenantNavLink extends NavLink<TenantNavId> {
  /** `null`: every member sees the link (the User Guide), matching the server's permission-less nav items. */
  readonly permission: PermissionKey | null;
  readonly agentScoped: boolean;
  /** The server marked the link plan-locked: it stays in the sidebar with a lock and opens the upgrade card. */
  readonly locked?: boolean;
}

export type TenantNavEntry = NavSection | TenantNavLink;
export type AdminNavEntry = NavSection | NavLink<AdminNavId>;

const section = (id: string, label: string): NavSection => ({ kind: "section", id, label, i18nKey: `nav.${id}` });

interface TenantLinkSpec {
  id: TenantNavId;
  label: string;
  icon: IconName;
  permission: PermissionKey | null;
  badgeSource?: NavBadgeSource;
  badgeTone?: NavBadgeTone;
}

const workspaceLink = (
  spec: TenantLinkSpec,
  build: (ws: string) => string,
): TenantNavLink => ({ kind: "link", agentScoped: false, i18nKey: `nav.${spec.id}`, ...spec, to: ({ ws }) => build(ws) });

const agentLink = (
  spec: TenantLinkSpec,
  build: (ws: string, agentId: string) => string,
): TenantNavLink => ({
  kind: "link",
  agentScoped: true,
  i18nKey: `nav.${spec.id}`,
  ...spec,
  to: ({ ws, agentId }) => build(ws, agentId),
});

const TENANT_NAV: readonly TenantNavEntry[] = [
  section("overview", "Overview"),
  workspaceLink({ id: "dashboard", label: "Dashboard", icon: "layout-dashboard", permission: "analytics.view" }, consoleRoutes.dashboard),
  workspaceLink(
    { id: "inbox", label: "Inbox", icon: "inbox", permission: "inbox.view", badgeSource: "inbox.open", badgeTone: "brand" },
    (ws) => consoleRoutes.inbox(ws),
  ),
  workspaceLink({ id: "analytics", label: "Analytics", icon: "bar-chart", permission: "analytics.view" }, consoleRoutes.analytics),
  section("build", "Build"),
  agentLink({ id: "playground", label: "Playground", icon: "messages-square", permission: "playground.use" }, consoleRoutes.playground),
  agentLink({ id: "knowledge", label: "Knowledge Base", icon: "database", permission: "knowledge.view" }, consoleRoutes.knowledge),
  workspaceLink({ id: "knowledge-graph", label: "Knowledge graph", icon: "git-branch", permission: "knowledge.view" }, (ws) => consoleRoutes.knowledgeGraph(ws)),
  agentLink(
    { id: "agent-settings", label: "Agent Settings", icon: "bot", permission: "agents.edit" },
    (ws, agentId) => consoleRoutes.agentSettings(ws, agentId),
  ),
  agentLink({ id: "actions", label: "Actions", icon: "zap", permission: "actions.view" }, consoleRoutes.actions),
  agentLink({ id: "evaluations", label: "Evaluations", icon: "flask", permission: "evaluations.view" }, consoleRoutes.evaluations),
  section("deploy", "Deploy"),
  agentLink(
    { id: "widget-studio", label: "Widget Studio", icon: "palette", permission: "widget.view" },
    (ws, agentId) => consoleRoutes.widgetStudio(ws, agentId),
  ),
  agentLink(
    { id: "webapp-studio", label: "Web App Studio", icon: "app-window", permission: "widget.view" },
    (ws, agentId) => consoleRoutes.webappStudio(ws, agentId),
  ),
  agentLink({ id: "channels", label: "Channels", icon: "plug", permission: "channels.view" }, consoleRoutes.channels),
  section("engage", "Engage"),
  workspaceLink(
    { id: "gaps", label: "Knowledge Gaps", icon: "help-circle", permission: "gaps.view", badgeSource: "gaps.open", badgeTone: "accent" },
    consoleRoutes.gaps,
  ),
  workspaceLink({ id: "leads", label: "Leads & Inquiries", icon: "user-check", permission: "leads.view" }, (ws) => consoleRoutes.leads(ws)),
  workspaceLink({ id: "workflows", label: "Workflows", icon: "workflow", permission: "workflows.view" }, consoleRoutes.workflows),
  workspaceLink({ id: "announcements", label: "Announcements", icon: "megaphone", permission: "announcements.view" }, consoleRoutes.announcements),
  workspaceLink({ id: "wallboard", label: "Wallboard", icon: "activity", permission: "wallboard.view" }, consoleRoutes.wallboard),
  section("service", "Service"),
  workspaceLink({ id: "tickets", label: "Tickets", icon: "ticket", permission: "tickets.view" }, (ws) => consoleRoutes.tickets(ws)),
  workspaceLink({ id: "organisations", label: "Organisations", icon: "building-2", permission: "organisations.view" }, (ws) => consoleRoutes.organisations(ws)),
  workspaceLink({ id: "contacts", label: "Contacts", icon: "contact", permission: "organisations.view" }, (ws) => consoleRoutes.contacts(ws)),
  workspaceLink({ id: "assets", label: "Assets", icon: "cpu", permission: "assets.view" }, (ws) => consoleRoutes.assets(ws)),
  workspaceLink({ id: "installations", label: "Installations", icon: "hard-hat", permission: "assets.view" }, (ws) => consoleRoutes.installations(ws)),
  workspaceLink({ id: "visits", label: "Field visits", icon: "truck", permission: "visits.view" }, (ws) => consoleRoutes.visits(ws)),
  workspaceLink({ id: "orders", label: "Orders", icon: "shopping-cart", permission: "orders.view" }, (ws) => consoleRoutes.orders(ws)),
  workspaceLink({ id: "catalogue", label: "Catalogue", icon: "package", permission: "orders.view" }, consoleRoutes.catalogue),
  workspaceLink({ id: "service-reports", label: "Service reports", icon: "chart-column", permission: "tickets.reports" }, (ws) => consoleRoutes.serviceReports(ws)),
  workspaceLink({ id: "service-settings", label: "Service settings", icon: "settings-2", permission: "tickets.configure" }, (ws) => consoleRoutes.serviceSettings(ws)),
  section("workspace", "Workspace"),
  workspaceLink(
    { id: "audiences", label: "Audiences & Access", icon: "shield-check", permission: "audiences.view" },
    (ws) => consoleRoutes.audiences(ws),
  ),
  workspaceLink({ id: "team", label: "Team", icon: "users", permission: "team.view" }, consoleRoutes.team),
  workspaceLink({ id: "billing", label: "Billing & Usage", icon: "credit-card", permission: "billing.view" }, consoleRoutes.billing),
  workspaceLink({ id: "settings", label: "Settings", icon: "settings", permission: "settings.view" }, (ws) => consoleRoutes.settings(ws)),
  section("help", "Help"),
  workspaceLink({ id: "guide", label: "User Guide", icon: "book-open", permission: null }, (ws) => consoleRoutes.guide(ws)),
];

const adminLink = (
  id: AdminNavId,
  label: string,
  icon: IconName,
  build: () => string,
  i18nKey = `nav.${id}`,
): NavLink<AdminNavId> => ({ kind: "link", id, label, i18nKey, icon, to: build });

const ADMIN_NAV: readonly AdminNavEntry[] = [
  section("platform", "Platform"),
  adminLink("dashboard", "Dashboard", "layout-dashboard", adminRoutes.dashboard),
  adminLink("tenants", "Tenants", "building", adminRoutes.tenants),
  adminLink("usage", "Usage & Costs", "activity", adminRoutes.usage),
  section("commercial", "Commercial"),
  adminLink("plans", "Plans & Pricing", "package", adminRoutes.plans),
  adminLink("sales-requests", "Sales requests", "inbox", adminRoutes.salesRequests),
  section("ai-platform", "AI Platform"),
  adminLink("ai-models", "AI Providers & Models", "cpu", () => adminRoutes.aiModels()),
  adminLink("system", "System Health", "server", adminRoutes.system),
  section("operations", "Operations"),
  { ...adminLink("support", "Support", "life-buoy", () => adminRoutes.support()), badgeSource: "support.open", badgeTone: "brand" },
  adminLink("audit", "Audit Logs", "scroll-text", adminRoutes.audit),
  adminLink("staff", "Platform Staff", "users", adminRoutes.staff),
  adminLink("settings", "Platform Settings", "settings", () => adminRoutes.settings(), "nav.platform-settings"),
];

export const NAV = {
  tenant: TENANT_NAV,
  admin: ADMIN_NAV,
} as const;

/** Tenant nav filtered to the links the current member may view. Empty sections are dropped. */
export function visibleTenantNav(granted: ReadonlySet<PermissionKey>): TenantNavEntry[] {
  const allowed = TENANT_NAV.filter((entry) => entry.kind === "section" || entry.permission === null || granted.has(entry.permission));
  return allowed.filter((entry, index) => {
    if (entry.kind === "link") return true;
    const next = allowed[index + 1];
    return next?.kind === "link";
  });
}

const TENANT_LINKS_BY_ID: ReadonlyMap<string, TenantNavLink> = new Map(
  TENANT_NAV.filter((entry): entry is TenantNavLink => entry.kind === "link").map((link) => [link.id, link]),
);

/** Server nav payload (`GET /v1/nav`): sections of item ids, already filtered by permission. */
export interface ServerNavSection {
  readonly id: string;
  readonly items: readonly { readonly id: string; readonly locked?: boolean }[];
}

/**
 * Renders exactly what the server returned, in its order. Ids the client does not know are
 * dropped; links the server omitted are never added back (P3 gate: server-filtered nav).
 */
export function tenantNavFromServer(sections: readonly ServerNavSection[]): TenantNavEntry[] {
  return sections.flatMap((serverSection) => {
    const links = serverSection.items.flatMap((item) => {
      const link = TENANT_LINKS_BY_ID.get(item.id);
      if (!link) return [];
      return [item.locked ? { ...link, locked: true } : link];
    });
    if (links.length === 0) return [];
    const label = TENANT_NAV.find((entry) => entry.kind === "section" && entry.id === serverSection.id)?.label ?? serverSection.id;
    return [section(serverSection.id, label), ...links];
  });
}
